// LeftPanel.tsx
import { FC, useEffect, useState } from "react";
import styled from "styled-components";
import Colors from "../../../ui/global/Colors";
import Fonts from "../../../ui/global/Fonts";
import InputSimple from "../../../ui/components/InputSimple";
import ImageUpload from "../../../ui/components/ImageUpload";
import Button from "../../../ui/components/Button";
import Slider from "../../../ui/components/Slider";
import LabelRow from "../../../ui/components/LabelRow";
import Checkbox from "../../../ui/components/Checkbox";
import MenuPanel from "../../../ui/components/MenuPanel";
import InputMicro from "../../../ui/components/InputMicro";
import Toggle from "../../../ui/components/Toggle";
import Tooltip from "../../../ui/components/Tooltip";
import ButtonLoad from "../../../ui/components/ButtonLoad";
import { AnimateActionType } from "../hooks/useAnimateActions";
import Info from "../../../ui/components/Info";
import { ClickAwayListener } from "@mui/material";

const LeftPanelContainer = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    height: 100%;
`;

const Content = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    gap: 14px;
    overflow-y: auto;
    flex-grow: 1;
    flex-shrink: 1;
`;

const ButtonContainer = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-end;
    width: 100%;
    gap: 6px;
`;

const Item = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    gap: 8px;
`;

const Row = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 0;
`;

const InstructionContainer = styled.div`
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    font-family: ${Fonts.lexendMedium.fontFamily};
    font-weight: ${Fonts.lexendMedium.fontWeight};
    font-size: 14px;
    line-height: 1.2em;
    border-radius: 6px;
    background-color: ${Colors.white};
    border: 1px solid ${Colors.grey1};
    padding: 4px 0 2px 0;
    color: ${Colors.black};
    gap: 10px;
    transition: border 0.2s ease;

    &:hover {
        border: 1px solid ${Colors.grey4};
    }

    &:focus-within {
        border: 1px solid ${Colors.grey6};
    }
`;

const DimensionInputsContainer = styled.div`
    display: flex;
    width: 100%;
    justify-content: space-between;
    gap: 4px;
`;

const TipText = styled.div`
    width: 100%;
    text-align: left;
    font-family: ${Fonts.lexendSemiLight.fontFamily};
    font-weight: ${Fonts.lexendSemiLight.fontWeight};
    font-size: 11px;
    line-height: 1.2em;
    color: ${Colors.grey6};
    padding-top: 4px;
    padding-bottom: 2px;
`;

const SingleRowItem = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    height: 15.6px;
`;

const Divider = styled.div<{ $isMobile?: boolean }>`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background-color: ${Colors.grey1};
    height: 1px;
    width: 100%;
    margin: 10px 6px 10px 0;
    // margin: ${({ $isMobile }) => $isMobile ? '4px 0 4px 6px' : '10px 6px 10px 0'};
`;

const UnsupportedBrowserMessage = styled.div`
    width: 100%;
    text-align: center;
    font-family: ${Fonts.lexendMedium.fontFamily};
    font-weight: ${Fonts.lexendMedium.fontWeight};
    font-size: 11px;
    line-height: 1.2em;
    color: ${Colors.red};
    text-wrap: balance;
`;

const TooltipInputContainer = styled.div<{ $isMobile: boolean }>`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    min-width: ${({ $isMobile }) => ($isMobile ? '100%' : '280px')};
    gap: 8px;

    & > button {
        position: absolute;
        bottom: 8px;
        right: 8px;
    }
`;

interface LeftPanelProps {
    config: any;
    imageUrl: string;
    descriptionText: string;
    handleImageChange: (imageUrl: string) => void;
    handleImageError: (error: string) => void;
    handleDescriptionChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
    onAnimateClick: () => void;
    currentAction: AnimateActionType | null;
    animationImages: string[];
    removeBackground: boolean;
    handleRemoveBackgroundChange: (newValue: boolean) => void;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    animationSpeed: number;
    handleAnimationSpeedChange: (newValue: number) => void;
    frameWidth: number | string;
    frameHeight: number | string;
    handleFrameWidthChange: (newValue: string | number) => void;
    handleFrameHeightChange: (newValue: string | number) => void;
    loop: boolean;
    handleLoopChange: (newValue: boolean) => void;
    progress: number;
    message: string;
    zoomOut: boolean;
    handleZoomOutChange: (newValue: boolean) => void;
    isProcessingImage: boolean;
    isMobile: boolean;
    handleGenerateImage: (prompt: string) => void;
    enhancePrompt: boolean;
    handleEnhancePromptChange: (newValue: boolean) => void;
}

const LeftPanel: FC<LeftPanelProps> = ({
    config,
    imageUrl,
    descriptionText,
    handleImageChange,
    handleImageError,
    handleDescriptionChange,
    onAnimateClick,
    currentAction,
    animationImages,
    removeBackground,
    handleRemoveBackgroundChange,
    open,
    onOpenChange,
    animationSpeed,
    handleAnimationSpeedChange,
    frameWidth,
    frameHeight,
    handleFrameWidthChange,
    handleFrameHeightChange,
    loop,
    handleLoopChange,
    progress,
    message,
    zoomOut,
    handleZoomOutChange,
    isProcessingImage,
    isMobile,
    handleGenerateImage,
    enhancePrompt,
    handleEnhancePromptChange,
}) => {
    const [showUnsupportedBrowserTip, setShowUnsupportedBrowserTip] = useState(false);
    const [showCreateImageTooltip, setShowCreateImageTooltip] = useState(false);
    const [createImagePrompt, setCreateImagePrompt] = useState("");

    useEffect(() => {
        const userAgent = navigator.userAgent.toLowerCase();
        const isFirefox = userAgent.includes('firefox');
        const isSafari = userAgent.includes('safari') && !userAgent.includes('chrome') && !userAgent.includes('chromium');
        
        if (isFirefox || isSafari) {
            setShowUnsupportedBrowserTip(true);
        }
    }, []);

    const buttonText = (currentAction === 'animate') ? "Animating..." : animationImages.length === 0 ? 'Animate' : "Re-Animate";
    const isButtonDisabled = (!imageUrl || !descriptionText.trim()) || (currentAction !== null) || isProcessingImage;

    const onGenerateImageClick = (prompt: string) => {
        // console.log("Create Image Clicked:", prompt);
        handleGenerateImage(prompt);
        setShowCreateImageTooltip(false);
    };
    
    return (
        <MenuPanel
            open={open}
            onOpenChange={onOpenChange}
            position="left"
            width={264}
            height="100%"
            collapsedWidth={0}
            colorMode="light"
            expandTooltip={null}
            // expandTooltip={open ? "Toolbar" : null}
            isCollapsible={true}
            fullBleed={true}
        >
            <LeftPanelContainer>
                <Content>
                    {/* <Item>
                        <InstructionContainer>
                            <InputSimple
                                value={''}
                                onChange={()=>{}}
                                placeholder="Object Name"
                                textAlign="left"
                                initialRows={1}
                            />
                        </InstructionContainer>
                    </Item> */}

                    {/* <Item>
                        <InstructionContainer>
                            <InputSimple
                                value={''}
                                onChange={()=>{}}
                                placeholder="Animation Name"
                                textAlign="left"
                                initialRows={1}
                            />
                        </InstructionContainer>
                    </Item> */}

                    <Item>
                        <LabelRow 
                            label="Reference Image" 
                            units="frame 1"
                        />
                        <ClickAwayListener onClickAway={() => setShowCreateImageTooltip(false)}>
                            <div style={{ width: '100%' }}>
                                <Tooltip
                                    open={showCreateImageTooltip}
                                    colorMode="light"
                                    position={isMobile ? "bottom" : "right"}
                                    content={
                                        <TooltipInputContainer $isMobile={isMobile}>
                                            {/* <LabelRow label="Create Image" /> */}
                                            <InstructionContainer>
                                                <InputSimple
                                                    value={createImagePrompt}
                                                    onChange={(e) => setCreateImagePrompt(e.target.value)}
                                                    placeholder="Describe your image..."
                                                    textAlign="left"
                                                    initialRows={5}
                                                />
                                            </InstructionContainer>
                                            <Button
                                                icon="arrow-slim-up"
                                                color="black"
                                                size="micro"
                                                disabled={!createImagePrompt.trim()}
                                                onClick={() => {
                                                    onGenerateImageClick(createImagePrompt)
                                                }}
                                            />
                                        </TooltipInputContainer>
                                    }
                                >
                                    <ImageUpload
                                        value={imageUrl}
                                        onChange={handleImageChange}
                                        onError={handleImageError}
                                        size="medium"
                                        placeholder={`Paste, drag & drop, or click to upload image`}
                                        maxSizeMB={5}
                                        acceptedTypes={["image/jpeg", "image/png", "image/gif", "image/webp"]}
                                        loading={isProcessingImage}
                                        button={config.ENABLE_ANIMATE_CREATE_IMAGE ? {
                                            text: 'Create Image',
                                            onClick: () => setShowCreateImageTooltip(true)
                                        } : undefined}
                                    />
                                </Tooltip>
                            </div>
                        </ClickAwayListener>
                        {/* <TipText>
                            TIP: For best results, make sure the reference image looks exactly like the first frame of the animation you want to create.
                        </TipText> */}
                        <SingleRowItem>
                            <Row>
                                <LabelRow label="Zoom-Out" />
                                <Info
                                    sub_text="Add padding to the reference image to reduce animation clipping."
                                    position="right"
                                    colorMode="light"
                                    tooltipColorMode="dark"
                                />
                            </Row>
                            {/* <Toggle
                                checked={zoomOut}
                                colorMode="light"
                                size="small"
                                onChange={handleZoomOutChange}
                            /> */}
                            <Checkbox
                                checked={zoomOut}
                                colorMode="light"
                                size={22}
                                onChange={(e, checked) => handleZoomOutChange(checked)}
                            />
                        </SingleRowItem>
                    </Item>

                    <Item>
                        {/* <LabelRow label="Animation Prompt" /> */}
                        <SingleRowItem>
                            <Row>
                                <LabelRow label="Animation Prompt" />
                            </Row>
                            <Tooltip
                                text="Enhance Prompt"
                                position="right"
                                colorMode="dark"
                            >
                                <Checkbox
                                    checked={enhancePrompt}
                                    colorMode="light"
                                    size={22}
                                    customIcon="sparkle"
                                    onChange={(e, checked) => handleEnhancePromptChange(checked)}
                                />
                            </Tooltip>
                        </SingleRowItem>
                        <InstructionContainer>
                            <InputSimple
                                value={descriptionText}
                                onChange={handleDescriptionChange}
                                placeholder="Walk, jump, fly..."
                                textAlign="left"
                                initialRows={5}
                            />
                        </InstructionContainer>
                        {/* <SingleRowItem>
                            <Row>
                                <LabelRow label="Enhance Prompt" />
                            </Row>
                            // <Toggle
                            //     checked={enhancePrompt}
                            //     colorMode="light"
                            //     size="small"
                            //     onChange={handleEnhancePromptChange}
                            // /> 
                            <Checkbox
                                checked={enhancePrompt}
                                colorMode="light"
                                size={22}
                                onChange={(e, checked) => handleEnhancePromptChange(checked)}
                            />
                        </SingleRowItem> */}
                    </Item>
                    
                    <Item>
                        <SingleRowItem>
                            <LabelRow label="Seamless Loop" />
                            <Tooltip
                                text={loop ? "On" : "Off"}
                                position="right"
                                colorMode="dark"
                            >
                                <Toggle
                                    checked={loop}
                                    onChange={handleLoopChange}
                                    colorMode="light"
                                    size="small"
                                />
                                {/* <Checkbox
                                    checked={loop}
                                    colorMode="light"
                                    size={22}
                                    onChange={(e, checked) => handleLoopChange(checked)}
                                /> */}
                            </Tooltip>
                        </SingleRowItem>
                    </Item>

                    {/* <Item>
                        <SingleRowItem>
                            <LabelRow label="Transparent Background" />
                            <Checkbox
                                checked={removeBackground}
                                colorMode="light"
                                size={22}
                                onChange={(e, checked) => handleRemoveBackgroundChange(checked)}
                            />
                        </SingleRowItem>
                    </Item> */}

                    {/* <Item>
                        <LabelRow
                            label='Frame Size'
                            units='px'
                        />
                        <DimensionInputsContainer>
                            <InputMicro
                                value={frameWidth}
                                onChange={handleFrameWidthChange}
                                colorMode='light'
                                labelLeft='W'
                                width='100%'
                                backgroundColor={Colors.white}
                            />
                            <InputMicro
                                value={frameHeight}
                                onChange={handleFrameHeightChange}
                                colorMode='light'
                                labelLeft='H'
                                width='100%'
                                backgroundColor={Colors.white}
                            />
                        </DimensionInputsContainer>
                    </Item> */}

                    {/* {(animationImages.length > 0) && (
                        <>
                        <Divider />

                        <Item>
                            <LabelRow label="Speed" value={animationSpeed.toString()} units="(FPS)" unitsPosition="left"/>
                            <Slider
                                value={animationSpeed}
                                min={1}
                                max={96}
                                step={1}
                                colorMode="light"
                                onChange={(newValue) => handleAnimationSpeedChange(newValue)}
                                showLabel={false}
                            />
                        </Item>
                        </>
                    )} */}
                </Content>

                <ButtonContainer>
                    {/* {showUnsupportedBrowserTip && (
                        <UnsupportedBrowserMessage>
                            Some features may not work on Safari or Firefox. Please use Chrome.
                        </UnsupportedBrowserMessage>
                    )} */}
                    <ButtonLoad
                        size="tiny"
                        color="black"
                        text={buttonText}
                        onClick={onAnimateClick}
                        fullWidth={true}
                        disabled={isButtonDisabled}
                        progress={currentAction === 'animate' ? progress : undefined}
                    />
                </ButtonContainer>
            </LeftPanelContainer>
        </MenuPanel>
    );
};

export default LeftPanel;